import { BrainCircuit, Check, Eye, Layers3, Sparkles, Store, Users, Workflow } from 'lucide-react'
import { Link } from 'react-router-dom'
import { BrandMark } from '../../components/BrandMark'

export const moduleCopy = [
  ['Business Knowledge Hub', 'Keeps the facts, offers, processes and brand rules DAWN is allowed to use in one organised place.', BrainCircuit],
  ['Marketing Assistant', 'Prepares campaign ideas, emails, social posts and creative briefs for a person to review.', Sparkles],
  ['Sales Support', 'Researches prospects and prepares meeting notes, follow-ups and next-step reminders.', Users],
  ['Market Watch', 'Tracks useful competitor and customer signals, then explains what may deserve attention.', Eye],
  ['Digital Product Builder', 'Turns approved knowledge into structured first drafts, worksheets and production packs.', Layers3],
  ['Website and Shop Checker', 'Checks customer journeys, product information and website issues, then prepares an improvement list.', Store],
  ['Operations Assistant', 'Keeps track of recurring jobs, deadlines, owners, problems and follow-through.', Workflow],
] as const

export const pricing = [
  {
    name: 'Starter',
    price: '£499',
    setup: '£1,500 one-off setup',
    description: 'For one clear business problem and a small number of repeat jobs.',
    features: ['One organised business knowledge area', 'Two areas of support', 'Up to five repeat workflows', 'One business workspace', 'Monthly review'],
  },
  {
    name: 'Growth',
    price: '£1,250',
    setup: '£5,000 one-off setup',
    description: 'For a growing team that wants help across several parts of the business.',
    features: ['Up to five areas of support', 'Workflows designed around your business', 'Team access and approvals', 'Usage and spending limits', 'Fortnightly improvement review'],
    featured: true,
  },
  {
    name: 'Managed Support',
    price: '£3,500',
    setup: '£12,000 one-off setup',
    description: 'For businesses that want us to help run and improve the system with them.',
    features: ['Full range of support areas', 'Hands-on workflow management', 'Priority setup and changes', 'Regular operating meetings', 'Custom reports'],
  },
  {
    name: 'Larger Organisations',
    price: 'Custom',
    setup: 'Agreed project price',
    description: 'For larger teams, sensitive information or more complicated systems and access needs.',
    features: ['Private setup options', 'More detailed access controls', 'Agreed data and retention rules', 'Priority support', 'Security and integration review'],
  },
]

export function SectionHeader({ title, copy, align = 'left' }: { title: string; copy: string; align?: 'left' | 'center' }) {
  return <div className={`section-header section-header--${align}`}><h2>{title}</h2><p>{copy}</p></div>
}

export function PricingSection({ compact = false }: { compact?: boolean }) {
  return (
    <section className={`section pricing-section ${compact ? 'pricing-section--compact' : ''}`}>
      <div className="container">
        <SectionHeader
          align="center"
          title="What does DAWN cost?"
          copy="There is a one-off setup cost to understand the business, connect approved tools and test the first workflows. The monthly fee covers the platform and agreed ongoing support. Any additional usage or custom work is discussed before it is added."
        />
        <div className="pricing-grid">
          {pricing.map(plan => <article className={plan.featured ? 'featured' : ''} key={plan.name}>
            {plan.featured && <span className="pricing-label">Most popular starting point</span>}
            <h3>{plan.name}</h3><p>{plan.description}</p><strong className="price">{plan.price === 'Custom' ? plan.price : <>from {plan.price}<small>/month</small></>}</strong><span className="setup">{plan.setup}</span>
            <ul>{plan.features.map(feature => <li key={feature}><Check size={15} />{feature}</li>)}</ul>
            <Link className={`button ${plan.featured ? 'button--gold' : 'button--outline'}`} to="/request-demo">Talk through this option</Link>
          </article>)}
        </div>
        <p className="pricing-clarifier">Not sure which option fits? Start by telling us which work is taking too long. We will recommend the smallest sensible starting point.</p>
      </div>
    </section>
  )
}

export function PageHero({ title, copy }: { title: string; copy: string }) {
  return <section className="page-hero"><div className="page-hero__orb" /><div className="container"><BrandMark compact light /><h1>{title}</h1><p>{copy}</p></div></section>
}
